// ==================== AGENT SETTINGS → EXTERNAL API PAYLOAD ====================

export interface ScoringWeights {
  industry_match: number;
  revenue_fit: number;
  location: number;
  digital_presence: number;
  firmographic_quality: number;
}

export interface LeadGenSettingsPayload {
  icp: {
    decision_maker_titles: string[];
    target_industries: string[];
    ownership_types: string[];
    revenue_min: number;
    revenue_max: number;
    growth_stage: string;
    primary_geography: string;
    min_fit_score: number;
    require_website: boolean;
    require_contact: boolean;
    scoring_weights: ScoringWeights;
  };
  outreach: {
    sender_domain: string;
    daily_send_limit: number;
    send_window_start: string;
    send_window_end: string;
    language_default: string;
    followup_enabled: boolean;
    reply_check_enabled: boolean;
    followup_max_attempts: number;
    followup_interval_hours: number;
  };
  ai_agent: {
    model: string;
    agent_mode: string;
    email_tone: string;
    hypothesis_depth: string;
    summary_depth: string;
  };
}

export interface FormWeights {
  industry: number;
  revenue: number;
  location: number;
  signals: number;
  data: number;
}

export interface AgentSettingsFormState {
  /** @deprecated not consumed by the agent — industries are chosen per campaign */
  selectedIndustries?: Set<string>;
  selectedOwnershipTypes: Set<string>;
  weights: FormWeights;
  agentMode: string;
  emailTone: string;
  language: string;
  /** @deprecated not consumed by the agent */
  growthStage?: string;
  /** @deprecated not consumed by the agent — location is chosen per campaign */
  primaryGeography?: string;
  aiModel: string;
  hypothesisDepth: string;
  summaryDepth: string;
  decisionMakerTitles?: string[];
  revenueMin?: number;
  revenueMax?: number;
  senderDomain?: string;
  dailySendLimit?: number;
  sendWindowStart?: string;
  sendWindowEnd?: string;
  minFitScore?: number;
  requireWebsite?: boolean;
  requireContact?: boolean;
  followupEnabled?: boolean;
  replyCheckEnabled?: boolean;
  followupMaxAttempts?: number;
  followupIntervalHours?: number;
}

const DECISION_MAKER_TITLES_DEFAULT = [
  'CEO',
  'COO',
  'GM',
  'Owner',
  'Managing Director',
  'Operations Director',
] as const;

const DEFAULT_SCORING_WEIGHTS: FormWeights = {
  industry: 30,
  revenue: 20,
  location: 20,
  signals: 15,
  data: 15,
};

const DEFAULT_OWNERSHIP_TYPES = ['Private', 'Family-owned'];

export const OWNERSHIP_TYPE_OPTIONS = ['Private', 'Family-owned', 'SME', 'Enterprise'] as const;

export function formatDecisionMakerTitles(titles: string[]): string {
  return titles.join(', ');
}

export function parseDecisionMakerTitles(input: string): string[] {
  return input
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export const INDUSTRY_TO_API: Record<string, string> = {
  Manufacturing: 'manufacturing',
  Logistics: 'logistics',
  Insurance: 'insurance',
  Construction: 'construction',
  Healthcare: 'healthcare',
  Retail: 'retail',
  'Real Estate': 'real_estate',
};

export const INDUSTRY_SLUGS = Object.values(INDUSTRY_TO_API);

export function slugIndustryLabel(label: string): string {
  return INDUSTRY_TO_API[label] ?? label.toLowerCase().replace(/\s+/g, '_');
}

const API_TO_INDUSTRY: Record<string, string> = Object.fromEntries(
  Object.entries(INDUSTRY_TO_API).map(([label, slug]) => [slug, label]),
);

const LANGUAGE_TO_API: Record<string, string> = {
  en: 'EN',
  ar: 'AR',
  bilingual: 'EN',
};

const API_TO_LANGUAGE: Record<string, string> = {
  EN: 'en',
  AR: 'ar',
};

const AGENT_MODE_TO_API: Record<string, string> = {
  'semi-autonomous': 'semi-autonomous',
  autonomous: 'autonomous',
};

const API_TO_AGENT_MODE: Record<string, string> = {
  'semi-autonomous': 'semi-autonomous',
  autonomous: 'autonomous',
  'fully-autonomous': 'autonomous',
};

function slugIndustry(label: string): string {
  return slugIndustryLabel(label);
}

function apiIndustryToLabel(slug: string): string {
  const normalized = slug.toLowerCase().replace(/\s+/g, '_');
  return API_TO_INDUSTRY[normalized] ?? slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseSenderDomain(emailOrDomain: string): string {
  const trimmed = emailOrDomain.trim();
  if (trimmed.includes('@')) return trimmed.split('@')[1] ?? trimmed;
  return trimmed;
}

function senderDomainToDisplay(domain: string): string {
  const trimmed = domain.trim();
  if (!trimmed) return 'outreach@yourcompany.com';
  if (trimmed.includes('@')) return trimmed;
  return `outreach@${trimmed}`;
}

function weightsFromApi(sw?: ScoringWeights): FormWeights {
  if (!sw) return { ...DEFAULT_SCORING_WEIGHTS };
  return {
    industry: sw.industry_match ?? DEFAULT_SCORING_WEIGHTS.industry,
    revenue: sw.revenue_fit ?? DEFAULT_SCORING_WEIGHTS.revenue,
    location: sw.location ?? DEFAULT_SCORING_WEIGHTS.location,
    signals: sw.digital_presence ?? DEFAULT_SCORING_WEIGHTS.signals,
    data: sw.firmographic_quality ?? DEFAULT_SCORING_WEIGHTS.data,
  };
}

function weightsToApi(weights: FormWeights): ScoringWeights {
  return {
    industry_match: weights.industry,
    revenue_fit: weights.revenue,
    location: weights.location,
    digital_presence: weights.signals,
    firmographic_quality: weights.data,
  };
}

export function formatRevenue(amount: number): string {
  if (!amount || amount <= 0) return '—';
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `$${Number.isInteger(millions) ? millions : millions.toFixed(1)}M`;
  }
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${amount}`;
}

function defaultIndustriesList(): string[] {
  return ['Manufacturing', 'Logistics', 'Construction', 'Healthcare', 'Retail'];
}

/** Map GET /settings response into React form state. */
export function fromSettingsPayload(payload: LeadGenSettingsPayload): AgentSettingsFormState {
  const { icp, outreach, ai_agent } = payload;

  const industries = (icp.target_industries ?? []).map(apiIndustryToLabel);
  const selectedIndustries = new Set(industries.length > 0 ? industries : defaultIndustriesList());

  const ownershipTypes = icp.ownership_types ?? [];
  const selectedOwnershipTypes = new Set(
    ownershipTypes.length > 0 ? ownershipTypes : DEFAULT_OWNERSHIP_TYPES,
  );

  return {
    selectedIndustries,
    selectedOwnershipTypes,
    weights: weightsFromApi(icp.scoring_weights),
    agentMode: API_TO_AGENT_MODE[ai_agent.agent_mode] ?? 'semi-autonomous',
    emailTone: ai_agent.email_tone || 'formal-business',
    language: API_TO_LANGUAGE[outreach.language_default] ?? 'en',
    growthStage: icp.growth_stage || 'Scaling',
    primaryGeography: icp.primary_geography || 'Karachi',
    aiModel: ai_agent.model || 'qwen2.5-coder:14b',
    hypothesisDepth: ai_agent.hypothesis_depth || 'concise',
    summaryDepth: ai_agent.summary_depth || 'standard',
    decisionMakerTitles: icp.decision_maker_titles?.length
      ? [...icp.decision_maker_titles]
      : [...DECISION_MAKER_TITLES_DEFAULT],
    revenueMin: icp.revenue_min ?? 0,
    revenueMax: icp.revenue_max ?? 0,
    senderDomain: senderDomainToDisplay(outreach.sender_domain ?? ''),
    dailySendLimit: outreach.daily_send_limit ?? 50,
    sendWindowStart: outreach.send_window_start ?? '09:00',
    sendWindowEnd: outreach.send_window_end ?? '17:00',
    minFitScore: icp.min_fit_score ?? 45,
    requireWebsite: icp.require_website ?? false,
    requireContact: icp.require_contact ?? false,
    followupEnabled: outreach.followup_enabled ?? true,
    replyCheckEnabled: outreach.reply_check_enabled ?? true,
    followupMaxAttempts: outreach.followup_max_attempts ?? 4,
    followupIntervalHours: outreach.followup_interval_hours ?? 48,
  };
}

export function toSettingsPayload(state: AgentSettingsFormState): LeadGenSettingsPayload {
  const ownershipTypes =
    state.selectedOwnershipTypes.size > 0
      ? Array.from(state.selectedOwnershipTypes)
      : [...DEFAULT_OWNERSHIP_TYPES];

  return {
    icp: {
      decision_maker_titles: [...(state.decisionMakerTitles ?? DECISION_MAKER_TITLES_DEFAULT)],
      target_industries: Array.from(state.selectedIndustries ?? []).map(slugIndustry),
      ownership_types: ownershipTypes,
      revenue_min: state.revenueMin ?? 0,
      revenue_max: state.revenueMax ?? 0,
      growth_stage: (state.growthStage ?? '').trim(),
      primary_geography: (state.primaryGeography ?? '').trim(),
      min_fit_score: state.minFitScore ?? 45,
      require_website: state.requireWebsite ?? false,
      require_contact: state.requireContact ?? false,
      scoring_weights: weightsToApi(state.weights),
    },
    outreach: {
      sender_domain: parseSenderDomain(state.senderDomain ?? 'yourcompany.com'),
      daily_send_limit: state.dailySendLimit ?? 50,
      send_window_start: state.sendWindowStart ?? '09:00',
      send_window_end: state.sendWindowEnd ?? '17:00',
      language_default: LANGUAGE_TO_API[state.language] ?? 'EN',
      followup_enabled: state.followupEnabled ?? true,
      reply_check_enabled: state.replyCheckEnabled ?? true,
      followup_max_attempts: state.followupMaxAttempts ?? 4,
      followup_interval_hours: state.followupIntervalHours ?? 48,
    },
    ai_agent: {
      model: state.aiModel,
      agent_mode: AGENT_MODE_TO_API[state.agentMode] ?? state.agentMode,
      email_tone: state.emailTone,
      hypothesis_depth: state.hypothesisDepth,
      summary_depth: state.summaryDepth || 'standard',
    },
  };
}
